import { useState, useCallback, ChangeEvent, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../../store/store';
import {
  addUser,
  editUser,
  EditUserRequest,
} from '../../../store/reducers/UsersSlice';
import { NewUserRequest } from '../../../store/reducers/UsersSlice';
import type { Role } from '../../../models/Users/Role';
import { UsersTablet } from '../../../models/Users/Users';
import { stripClinicSelectAllValue } from '../../shared/useClinicSelectAllOptions';

interface UseAddUserPopupProps {
  roles: readonly Role[];
  selectedUser?: UsersTablet;
  isEditing?: boolean;
  open?: boolean;
}

export const useAddUserPopup = ({
  roles,
  selectedUser,
  isEditing,
  open,
}: UseAddUserPopupProps) => {
  const dispatch = useAppDispatch();

  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLockout, setIsLockout] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);

  const prevUserIdRef = useRef<string | undefined>(undefined);

  const handleReset = useCallback(() => {
    setUserName('');
    setPassword('');
    setIsLockout(false);
    setSelectedRole('');
    setSelectedClinics([]);
  }, []);

  useEffect(() => {
    if (!open) {
      handleReset();
      prevUserIdRef.current = undefined;
      return;
    }

    if (isEditing && selectedUser) {
      const currentUserId = selectedUser.id;

      if (currentUserId !== prevUserIdRef.current) {
        setUserName(selectedUser.userName || '');
        setIsLockout(selectedUser.isLockout ?? false);
        setSelectedRole(String(selectedUser.role?.id || ''));
        setSelectedClinics(
          (selectedUser.clinics || []).map((c) => String(c.id)),
        );
        prevUserIdRef.current = currentUserId;
      }
    } else if (!isEditing) {
      handleReset();
      prevUserIdRef.current = undefined;
    }
  }, [open, isEditing, selectedUser, handleReset]);

  const setUserNameValue = (event: ChangeEvent<HTMLInputElement>) => {
    setUserName(event.target.value);
  };

  const setPasswordValue = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const setIsLockoutValue = (event: boolean) => {
    setIsLockout(event);
  };

  const setSelectedRoleValue = (value: string) => {
    setSelectedRole(value);
  };

  const setSelectedClinicsValue = (value: string[]) => {
    setSelectedClinics(stripClinicSelectAllValue(value));
  };

  const selectedRoleName =
    roles.find((role) => role.id === selectedRole)?.name ??
    (selectedUser?.role.id === selectedRole
      ? selectedUser.role.name
      : undefined);
  const isAdmin = selectedRoleName === 'Admin';

  const handleOk = useCallback(() => {
    const userRequest: NewUserRequest = {
      userName,
      password,
      isLockout,
      roleId: selectedRole,
      clinicId: isAdmin ? [] : stripClinicSelectAllValue(selectedClinics),
    };

    return dispatch(addUser(userRequest));
  }, [
    dispatch,
    userName,
    password,
    isLockout,
    selectedRole,
    selectedClinics,
    isAdmin,
  ]);

  const handleEditOk = useCallback(() => {
    const userRequest: EditUserRequest = {
      id: selectedUser?.id || '',
      isLockout,
      roleId: selectedRole,
      clinicId: isAdmin ? [] : stripClinicSelectAllValue(selectedClinics),
    };

    return dispatch(editUser(userRequest));
  }, [
    dispatch,
    isLockout,
    selectedRole,
    selectedClinics,
    selectedUser,
    isAdmin,
  ]);

  return {
    userName,
    password,
    selectedRole,
    selectedClinics,
    isLockout,
    onUserNameChange: setUserNameValue,
    onPasswordChange: setPasswordValue,
    onPasswordGenerated: setPassword,
    onSelectedRoleChange: setSelectedRoleValue,
    onSelectedClinicChange: setSelectedClinicsValue,
    onIsLockoutChange: setIsLockoutValue,
    handleOk,
    handleEditOk,
    handleReset,
  };
};
